import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { MessageCircle, ArrowRight, MapPin, Phone, Mail, Clock } from "lucide-react";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { BRAND, INTERESTED_IN_OPTIONS } from "@/lib/data";
import { buildWhatsAppUrl, buildMailtoUrl } from "@/lib/whatsapp";
import { FORM, HOME } from "@/constants/testIds";

const EMPTY = {
    fullName: "",
    country: "",
    email: "",
    phone: "",
    enquiryType: "Retail",
    interestedIn: "",
    budget: "",
    message: "",
};

function readInitialForm() {
    try {
        const params = new URLSearchParams(window.location.search);
        const cat = params.get("category");
        if (cat) return { ...EMPTY, interestedIn: cat };
    } catch (_) {
        /* noop */
    }
    return EMPTY;
}

/**
 * Full-width Private Consultation section:
 *  - Left: dedicated enquiry form (inline, not a modal)
 *  - Right: contact details (address, phone, email, hours)
 * Supports two send channels: WhatsApp (wa.me) and Email (mailto:).
 */
export default function PrivateConsultation() {
    const [form, setForm] = useState(readInitialForm);
    const location = useLocation();

    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const cat = params.get("category");
        if (cat) {
            setForm((prev) => (prev.interestedIn === cat ? prev : { ...prev, interestedIn: cat }));
        }
    }, [location.search, location.hash, location.key]);

    const set = (key) => (e) => {
        const value = e && e.target ? e.target.value : e;
        setForm((prev) => ({ ...prev, [key]: value }));
    };

    const validate = () => {
        if (!form.fullName.trim()) {
            toast.error("Please share your name.");
            return false;
        }
        if (!form.message.trim()) {
            toast.error("Please add a short message so we can help.");
            return false;
        }
        return true;
    };

    const sendWhatsApp = (e) => {
        e && e.preventDefault && e.preventDefault();
        if (!validate()) return;
        window.open(buildWhatsAppUrl(form), "_blank", "noopener,noreferrer");
        toast.success("Opening WhatsApp with your enquiry…");
        setForm(EMPTY);
    };

    const sendEmail = (e) => {
        e && e.preventDefault && e.preventDefault();
        if (!validate()) return;
        // Use window.location for mailto — better mail-client compatibility than window.open
        window.location.href = buildMailtoUrl(form);
        toast.success("Opening your email client…");
        // Do not clear form immediately — some browsers keep the tab; give a small delay
        setTimeout(() => setForm(EMPTY), 400);
    };

    return (
        <section
            id="consultation"
            data-testid={HOME.whatsappCta}
            className="premium-dark-bg text-[#F1F1EE] px-6 md:px-12 py-24 md:py-36 relative overflow-hidden scroll-mt-28"
        >
            <div className="relative z-10 max-w-[1500px] mx-auto">
                {/* Header */}
                <div className="max-w-3xl">
                    <p className="overline text-[#D4AF37]">Private Consultation</p>
                    <h2 className="font-serif-display text-4xl md:text-6xl lg:text-7xl mt-6 leading-[0.98] text-[#F1F1EE]">
                        Looking for something<br />
                        <span className="italic text-[#F1F1EE]/95">truly unique?</span>
                    </h2>
                    <div className="w-10 h-px bg-[#D4AF37] mt-8" />
                    <p className="mt-8 text-[#B8B8B8] font-light text-base md:text-lg leading-relaxed max-w-xl">
                        Speak directly with our team for personalised recommendations and private consultations. Share a few details and we&rsquo;ll continue the conversation on WhatsApp or by email.
                    </p>
                </div>

                <div className="mt-16 md:mt-20 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
                    {/* Form */}
                    <form
                        onSubmit={sendWhatsApp}
                        className="lg:col-span-8"
                        data-testid="consultation-form"
                        noValidate
                    >
                        {/* Retail / Wholesale toggle */}
                        <div className="mb-10">
                            <span className="dark-label">Enquiry Type</span>
                            <div className="segmented" data-testid="enquiry-type-group" role="tablist">
                                {["Retail", "Wholesale"].map((t) => (
                                    <button
                                        key={t}
                                        type="button"
                                        role="tab"
                                        aria-selected={form.enquiryType === t}
                                        data-testid={`enquiry-type-${t.toLowerCase()}`}
                                        onClick={() => setForm((p) => ({ ...p, enquiryType: t }))}
                                        className={form.enquiryType === t ? "active" : ""}
                                    >
                                        {t}
                                    </button>
                                ))}
                            </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-7">
                            <div>
                                <label className="dark-label" htmlFor="cf-fn">Full Name *</label>
                                <input
                                    id="cf-fn"
                                    data-testid={FORM.fullName}
                                    type="text"
                                    value={form.fullName}
                                    onChange={set("fullName")}
                                    className="dark-input"
                                    placeholder="Your name"
                                    autoComplete="name"
                                />
                            </div>
                            <div>
                                <label className="dark-label" htmlFor="cf-country">Country</label>
                                <input
                                    id="cf-country"
                                    data-testid={FORM.country}
                                    type="text"
                                    value={form.country}
                                    onChange={set("country")}
                                    className="dark-input"
                                    placeholder="e.g. United Kingdom"
                                    autoComplete="country-name"
                                />
                            </div>
                            <div>
                                <label className="dark-label" htmlFor="cf-email">Email</label>
                                <input
                                    id="cf-email"
                                    data-testid={FORM.email}
                                    type="email"
                                    value={form.email}
                                    onChange={set("email")}
                                    className="dark-input"
                                    placeholder="you@example.com"
                                    autoComplete="email"
                                />
                            </div>
                            <div>
                                <label className="dark-label" htmlFor="cf-phone">Phone</label>
                                <input
                                    id="cf-phone"
                                    data-testid={FORM.phone}
                                    type="tel"
                                    value={form.phone}
                                    onChange={set("phone")}
                                    className="dark-input"
                                    placeholder="+—"
                                    autoComplete="tel"
                                />
                            </div>
                            <div>
                                <label className="dark-label">Interested In</label>
                                <Select value={form.interestedIn} onValueChange={set("interestedIn")}>
                                    <SelectTrigger
                                        data-testid={FORM.interestedInTrigger}
                                        className="dark-input rounded-none h-auto shadow-none pl-0 pr-0 focus:ring-0 focus:ring-offset-0 text-[#F1F1EE] [&_svg]:text-[#F1F1EE]/70 [&>span]:text-[#F1F1EE] data-[placeholder]:[&>span]:text-[#F1F1EE]/40"
                                    >
                                        <SelectValue placeholder="Select a category" />
                                    </SelectTrigger>
                                    <SelectContent
                                        data-testid={FORM.interestedIn}
                                        className="bg-[#0A0A0A] border border-[#1F1F1F] rounded-none text-[#F1F1EE]"
                                    >
                                        {INTERESTED_IN_OPTIONS.map((opt) => (
                                            <SelectItem
                                                key={opt}
                                                value={opt}
                                                data-testid={`consultation-interested-${opt.toLowerCase().replace(/\s+/g, "-")}`}
                                                className="rounded-none font-sans focus:bg-[#141414] focus:text-[#D4AF37]"
                                            >
                                                {opt}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                            <div>
                                <label className="dark-label" htmlFor="cf-budget">Budget</label>
                                <input
                                    id="cf-budget"
                                    data-testid={FORM.budget}
                                    type="text"
                                    value={form.budget}
                                    onChange={set("budget")}
                                    className="dark-input"
                                    placeholder="e.g. USD 5,000 – 15,000"
                                />
                            </div>
                            <div className="md:col-span-2">
                                <label className="dark-label" htmlFor="cf-msg">Message *</label>
                                <textarea
                                    id="cf-msg"
                                    data-testid={FORM.message}
                                    value={form.message}
                                    onChange={set("message")}
                                    rows={5}
                                    className="dark-input resize-none"
                                    placeholder="Tell us what you're looking for — a piece, a room, a project…"
                                />
                            </div>
                        </div>

                        <div className="mt-10 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-6">
                            <p className="text-xs text-[#777777] font-light max-w-sm">
                                Your enquiry opens a pre-drafted message.<br />
                                WhatsApp&nbsp;<span className="text-[#F1F1EE]">{BRAND.phone}</span> &nbsp;·&nbsp; Email&nbsp;<span className="text-[#F1F1EE]">{BRAND.email}</span>
                            </p>
                            <div className="flex flex-col sm:flex-row gap-3">
                                <button
                                    type="button"
                                    onClick={sendEmail}
                                    data-testid="whatsapp-form-submit-email"
                                    className="btn-secondary group"
                                >
                                    <Mail className="w-4 h-4" strokeWidth={1.6} />
                                    Send via Email
                                </button>
                                <button
                                    type="submit"
                                    data-testid={FORM.submit}
                                    className="btn-primary group"
                                >
                                    <MessageCircle className="w-4 h-4" strokeWidth={1.6} />
                                    Send via WhatsApp
                                    <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" strokeWidth={1.6} />
                                </button>
                            </div>
                        </div>
                    </form>

                    {/* Contact panel */}
                    <aside
                        data-testid="consultation-contact"
                        className="lg:col-span-4 lg:pl-10 lg:border-l lg:border-[#1F1F1F]"
                    >
                        <p className="overline text-[#D4AF37]">Contact</p>
                        <p className="font-serif-display text-3xl md:text-4xl mt-6 leading-tight text-[#F1F1EE]">
                            Reach us directly.
                        </p>
                        <div className="w-8 h-px bg-[#D4AF37] mt-6" />

                        <ul className="mt-10 space-y-8">
                            <li>
                                <p className="dark-label mb-2">Studio &amp; Showroom</p>
                                <div className="flex items-start gap-3 text-[#EAEAEA] font-light leading-relaxed">
                                    <MapPin className="w-4 h-4 mt-1 flex-shrink-0 text-[#D4AF37]" strokeWidth={1.4} />
                                    <span>{BRAND.address}</span>
                                </div>
                            </li>
                            <li>
                                <p className="dark-label mb-2">WhatsApp &amp; Call</p>
                                <a
                                    href={buildWhatsAppUrl("Hello The Treasure Threads, I would like to enquire about your collection.")}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="flex items-center gap-3 text-[#EAEAEA] hover:text-[#D4AF37] transition-colors"
                                    data-testid="consultation-whatsapp"
                                >
                                    <Phone className="w-4 h-4 flex-shrink-0 text-[#D4AF37]" strokeWidth={1.4} />
                                    <span>{BRAND.phone}</span>
                                </a>
                            </li>
                            <li>
                                <p className="dark-label mb-2">Email</p>
                                <a
                                    href={`mailto:${BRAND.email}`}
                                    className="flex items-center gap-3 text-[#EAEAEA] hover:text-[#D4AF37] transition-colors break-all"
                                    data-testid="consultation-email"
                                >
                                    <Mail className="w-4 h-4 flex-shrink-0 text-[#D4AF37]" strokeWidth={1.4} />
                                    <span>{BRAND.email}</span>
                                </a>
                            </li>
                            <li>
                                <p className="dark-label mb-2">Response Time</p>
                                <div className="flex items-center gap-3 text-[#EAEAEA] font-light">
                                    <Clock className="w-4 h-4 flex-shrink-0 text-[#D4AF37]" strokeWidth={1.4} />
                                    <span>Usually within a few hours</span>
                                </div>
                            </li>
                        </ul>

                        <div className="mt-12 pt-10 border-t border-[#1F1F1F]">
                            <p className="overline text-[#888888]">Established</p>
                            <p className="font-serif-display text-2xl mt-3 text-[#F1F1EE]">Early 1980s · Chiang Mai</p>
                        </div>
                    </aside>
                </div>
            </div>
        </section>
    );
}
